/**
 * API client for audio transcription
 */

export interface TranscriptionResponse {
  text: string;
  success?: boolean;
  error?: string;
}

/**
 * Transcribes an audio blob to text using the backend API
 *
 * @param audioBlob - The audio blob to transcribe
 * @param apiEndpoint - Optional custom endpoint (defaults to http://localhost:3000/ai/transcript)
 * @returns The transcribed text
 * @throws Error if transcription fails
 *
 * @example
 * ```typescript
 * const audioBlob = await stopRecording();
 * const text = await transcribeAudio(audioBlob);
 * setInputValue(text);
 * ```
 */
export async function transcribeAudio(
  audioBlob: Blob,
  apiEndpoint?: string
): Promise<string> {
  const endpoint = apiEndpoint || 'http://localhost:3000/ai/transcript';

  try {
    // Create FormData for file upload
    const formData = new FormData();

    // Determine file extension based on blob type
    const mimeType = audioBlob.type || 'audio/webm';
    let extension = 'webm';

    if (mimeType.includes('webm')) {
      extension = 'webm';
    } else if (mimeType.includes('mp4')) {
      extension = 'mp4';
    } else if (mimeType.includes('ogg')) {
      extension = 'ogg';
    } else if (mimeType.includes('mpeg') || mimeType.includes('mp3')) {
      extension = 'mp3';
    }

    // Create a File object from the Blob
    const audioFile = new File(
      [audioBlob],
      `recording.${extension}`,
      { type: mimeType }
    );

    formData.append('audio', audioFile);

    // Send the audio to the API
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      // Try to parse error response
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorData?.message || errorMessage;
      } catch {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        } catch {
          // Keep default error message
        }
      }

      throw new Error(`Transcription failed: ${errorMessage}`);
    }

    // Parse the response
    const data: TranscriptionResponse = await response.json();

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response structure from transcription API');
    }

    // Check for error in response
    if (data.error) {
      throw new Error(data.error);
    }

    // Extract the transcribed text
    const text = data.text;

    if (!text || typeof text !== 'string') {
      throw new Error('No transcribed text received from API');
    }

    return text.trim();

  } catch (error) {
    console.error('Transcription error:', error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error('Unknown error occurred during transcription');
  }
}

/**
 * Check if the transcription API is available
 *
 * @param apiEndpoint - Optional custom endpoint
 * @returns true if the API is available
 */
export async function checkTranscriptionApiAvailable(
  apiEndpoint?: string
): Promise<boolean> {
  const endpoint = apiEndpoint || 'http://localhost:3000/ai/transcript';

  try {
    const response = await fetch(endpoint, {
      method: 'OPTIONS',
    });

    return response.ok;
  } catch {
    return false;
  }
}
